/** Maximum number of rep email recipients per RFP. */
export const MAX_REP_EMAILS = 10;

/** The candidate source value when added via sourcing search RFP. */
export const RFP_CANDIDATE_SOURCE = "sourcing-search-rfp";

/**
 * Peak guestroom threshold below which a Northstar RFP is handed off to the
 * client and hotel directly instead of being brokered by Nowadays.
 * Distinct from `MIN_ROOMS_PER_NIGHT` in create-event-v2/_logic/constants.ts,
 * which gates a different concern (wizard sourcing minimum).
 */
export const HANDOFF_ROOM_THRESHOLD = 50;

/**
 * Hand-off explanation shown to the hotel as an intro paragraph (before the
 * Event Brief, alongside the shortlist note); the submit-proposal link/button
 * section is omitted entirely on hand-off sends. Shared verbatim by both the
 * HTML (wrapped in `<p>…</p>`) and plaintext RFP email variants so the two
 * can't drift apart in wording.
 */
export const HANDOFF_EMAIL_BODY =
  "Our client is CC'd on this email and will coordinate with you on dates, availability, and any follow-up. As our agency remains the booking source for this program, we do ask that you extend commissionable rates. Please keep us CC'd on all correspondence so we stay in the loop.";

/**
 * Hand-off disclosure shown to the PLANNER before they send, in both the single-send and bulk
 * confirmation modals. Lives here beside {@link HANDOFF_EMAIL_BODY} so a reword reaches both
 * surfaces: two hand-written copies meant someone greps `HANDOFF_ROOM_THRESHOLD`, finds one, and
 * the other keeps acknowledging the old wording.
 *
 * @param venueLabel - How to refer to the recipient(s), e.g. a venue name or "each venue".
 * @param isPlural - Whether more than one request is going out, for "request"/"requests".
 * @returns The disclosure sentence.
 */
export const handoffDisclosure = (venueLabel: string, isPlural: boolean): string =>
  `For events under ${HANDOFF_ROOM_THRESHOLD} rooms, you'll work with ${venueLabel} directly on quote and rates. We'll send your request${isPlural ? "s" : ""} and CC you on ${isPlural ? "them" : "it"} so you can take it from there.`;

// The stamp sanitizer lives in `lib/rfp`: event details reads and writes the
// same chip, so the rule has one home. Re-exported for this feature's callers.
export { MAX_CUSTOM_LOCATION_FILTER_LENGTH, sanitizeCustomLocationFilter } from "@/lib/rfp/custom-location-filter";

/**
 * Max concurrent {@link requestProposal} calls when sending a batch of RFPs. Each send is an
 * outbound email plus several Supabase writes, so an unbounded fan-out over a large selection
 * would spike the connection pool and the mail provider's rate limit together.
 */
export const BULK_REQUEST_CONCURRENCY = 6;

/**
 * Most venues one bulk send will take at a time. Every send is a real email to a real hotel, so
 * the selection UI stops accepting more past this point rather than letting one pass fan out
 * indefinitely — a dense downtown viewport can hold 200 results.
 */
export const MAX_BULK_RFP_SELECTION = 10;

/**
 * Trailing window for the per-event RFP send ceiling. See
 * {@link MAX_RFP_SENDS_PER_EVENT_PER_WINDOW}.
 */
export const RFP_SEND_WINDOW_MS = 60 * 60 * 1000;

/**
 * Most RFP emails one event may send inside {@link RFP_SEND_WINDOW_MS}, enforced server-side in
 * `sendRfpToVenue` before any side effect.
 *
 * Counts actual sends (`venue_communications` rows), not distinct venues — see
 * {@link countRecentRfpSends} for why that distinction is the whole guard.
 *
 * Distinct from {@link MAX_BULK_RFP_SELECTION}, which is a UI affordance and therefore not a
 * boundary: the send action is reachable directly by any authenticated user with access to the
 * event, for any `venue_static_id`, so without this a script could walk `venue_static` and mail
 * every hotel in it from our sending domain.
 *
 * Set well above what a planner does by hand — six full batches an hour on one event — so it
 * bounds abuse without ever being the reason a real sourcing session stops.
 */
export const MAX_RFP_SENDS_PER_EVENT_PER_WINDOW = 60;

/**
 * Most RFP emails one USER may send inside {@link RFP_SEND_WINDOW_MS}, across every event.
 *
 * {@link MAX_RFP_SENDS_PER_EVENT_PER_WINDOW} alone is escaped by creating another event and
 * getting a fresh allowance, so the per-event ceiling bounds a mistake while this one bounds a
 * person. Higher than the per-event figure because sourcing several events in an afternoon is
 * ordinary work; twelve full batches an hour across all of them is not.
 */
export const MAX_RFP_SENDS_PER_USER_PER_WINDOW = 120;

/**
 * How long {@link BulkRfpModal} stays open after a fully successful batch, before handing off to
 * the toast (or the first-RFP education modal).
 *
 * Exists for the live region, not for looks: closing in the same tick as the success announcement
 * unmounts the `role="status"` node before assistive tech can observe the mutation, so a screen
 * reader user heard "sending 10 of 10" and then silence for the one irreversible action in the
 * flow. The ✓ chips being readable for a beat is a fair trade on top.
 */
export const OUTCOME_ANNOUNCEMENT_HOLD_MS = 1500;

/**
 * Marks the element `BulkRfpModal` hands focus back to when the opener it was launched from is
 * no longer focusable.
 *
 * A named contract rather than a CSS selector reaching into a sibling's markup. The modal's
 * fallback used to be `opener.closest('[role="region"]')`, which silently encoded
 * `SearchSelectionBar`'s internals: change that role, or wrap the trigger in any other region,
 * and focus lands somewhere arbitrary with nothing failing at compile time. Both files reference
 * this instead, so the coupling is at least visible from both ends.
 */
export const BULK_RFP_FOCUS_FALLBACK_ATTR = "data-bulk-rfp-focus-fallback";

/**
 * Most venues named inline in an outcome sentence before it falls back to a bare count.
 *
 * Three fits in a toast and stays a sentence; ten is a wall. The names matter because the two
 * buckets that get them — unconfirmed and undeliverable — are the ones the planner has to chase by
 * hand, and a count alone is unactionable once the modal is closed.
 */
export const NAMED_VENUE_LIMIT = 3;

/**
 * Bounds on the staff-authored RFP overrides the admin Venues tab composer can apply
 * (`RfpSendOverrides`). Enforced server-side in `sendRfpToVenue`'s own validation, not only in
 * the composer that renders the fields - a server action takes whatever it is handed.
 */
export const MAX_OVERRIDE_SUBJECT_CHARS = 200;
export const MAX_OVERRIDE_NOTE_CHARS = 4_000;
export const MAX_RFP_ATTACHMENTS = 10;
/** SendGrid rejects a message over 30 MB including its base64 overhead; this is the pre-encode
 *  budget that keeps a fully-loaded RFP under it. */
export const MAX_RFP_ATTACHMENT_TOTAL_BYTES = 20 * 1024 * 1024;

/**
 * Stand-in for the proposal-submission token in a PREVIEW render.
 *
 * A preview must not mint a real token: issuing one writes a `quote_requests` row, so browsing a
 * selection's previews and then cancelling would leave a row per venue for an RFP nobody sent.
 * When a live request already exists the preview shows its real URL; only when there is none does
 * the link fall back to this, which keeps the email's shape honest while naming itself as unsent.
 */
export const RFP_PREVIEW_TOKEN_PLACEHOLDER = "preview-not-yet-sent";

/**
 * Shown when the admin palette's "Send RFPs to bookmarked venues" finds no bookmarked venue in the
 * results that can still take an RFP. Multi-select stays off.
 */
export const BOOKMARKED_RFP_BATCH_NONE_MESSAGE = "No bookmarked venue in these results is waiting for an RFP.";
